import cn from 'classnames'
import { usePartnerId } from 'legacy/lut/classes/helpers/partner360/usePartnerId'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { P360NotInScopeWidgetPlaceholder } from 'legacy/lut/components/widgets/public/partner360/components/p360NotInScopeWidgetPlaceholder/P360NotInScopeWidgetPlaceholder'
import { ApplyButton } from 'legacy/lut/components/widgets/public/partner360/editors/ApplyButton'
import { HelpButton, HelpSection } from 'legacy/lut/components/widgets/public/partner360/helpButton'
import { P360Card } from 'legacy/lut/components/widgets/public/partner360/p360Card'
import { PartnerGroup } from 'legacy/lut/components/widgets/public/partner360/partnerGroup/PartnerGroup'
import { useWizardSave } from 'legacy/lut/components/widgets/public/partner360/planningWizard/context/WizardSaveContext'
import {
  createBasicInfo,
  createDefaultBasicInfo,
  IFormBasicInfo,
  isBasicInfoDistributor,
  isBasicInfoLargeFormat,
  isBasicInfoReseller,
  isBasicInfoRetail,
  selectBasicInfoStatus,
  selectCreatedBasicInfo,
  selectDefaultBasicInfo,
  TBasicInfoCreated,
  updateBasicInfo,
  updateDefaultBasicInfo,
} from 'legacy/lut/features/partner360/basicInfo'
import { requestCommitmentStatuses } from 'legacy/lut/features/partner360/commitment'
import { requestRecalculateCompleteness } from 'legacy/lut/features/partner360/completeness'
import {
  selectConfigurationLoadingStatus,
  selectFinancialYear,
  selectP360Profile,
  selectPartner360Data,
  selectPartnerAccess,
  selectProgramType,
} from 'legacy/lut/features/partner360/configuration'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useUserRoles } from 'legacy/lut/hooks/useUserRoles'
import { initJBPPartnerData } from 'legacy/lut/services/partner360/basicInfo/initJBPPartnerData'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { RequestStatus } from 'clickNgo/configs/service/common'
import { resetAllModifiedDataByKey, useGetFormData } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Loader } from 'core-components/library/loader'
import { selectAppOwnerId } from 'core-components/sitebuilder/store/selectors/selectAppOwnerId'
import MeasureController from 'core/analytics/MeasureController'
import { useMeasureController } from 'core/analytics/useMeasureController'
import { selectPublishingMode } from 'core/store/selectors/selectPublishingMode'
import { P360Access } from 'partner360/components/p360Access'
import { useRefetchPlanningCockpitData } from 'partner360/features/financialCockpit/model/hooks/useRefetchPlanningCockpitData'
import { selectIsCockpitPartnerDetailsVisible } from 'partner360/features/financialCockpit/model/selectors'
import { CockpitCardHeader } from 'partner360/features/financialCockpit/ui/components/cockpitCardHeader/CockpitCardHeader'
import { useHoldPlanSectionOpen } from 'partner360/lib/planSectionBusy'

import { withPartner360Checks } from '../../hoc/withPartner360Checks'

import { BasicInfoSaveContext } from './BasicInfoSaveContext'
import { BasicInfoHeader } from './components/basicInfoHeader/BasicInfoHeader'
import { ConfirmRevenueDialog } from './components/confirmRevenueScaleDialog/ConfirmRevenueDialog'
import { Branches } from './components/partnerFields/branches/Branches'
import { DemoUnits } from './components/partnerFields/demoUnits/DemoUnits'
import { DistributorInfo } from './components/partnerFields/distributorInfo/DistributorInfo'
import { ExecutiveSummary } from './components/partnerFields/executiveSummary/ExecutiveSummary'
import { OneKeyDirection } from './components/partnerFields/oneKeyDirection/OneKeyDirection'
import { OperatingProfit } from './components/partnerFields/operatingProfit/OperatingProfit'
import { PartnerHead } from './components/partnerFields/partnerHead/PartnerHead'
import { ProductExhibits } from './components/partnerFields/productExhibits/ProductExhibits'
import { RetailerFinance } from './components/partnerFields/retailerFinance/RetailerFinance'
import { RetailerStores } from './components/partnerFields/retailerStores/RetailerStores'
import { RetailerSummary } from './components/partnerFields/retailerSummary/RetailerSummary'
import { RetailInfo } from './components/partnerFields/retailInfo/RetailInfo'
import { ServiceDelivery } from './components/partnerFields/serviceDelivery/ServiceDelivery'
import { Showrooms } from './components/partnerFields/showrooms/Showrooms'
import { Ticker } from './components/partnerFields/ticker/Ticker'
import { Turnover } from './components/partnerFields/turnover/Turnover'
import { Warehouses } from './components/partnerFields/warehouses/Warehouses'
import { PARTNER_BASIC_INFO_FORM_KEY } from './configs'
import { TRANSLATION_KEYS } from './translations'

import styles from './PartnerBasicInfo.module.scss'

interface IPartnerBasicInfoProps {
  widgetParams: IObjectAny
  pid?: string
  metrics: MeasureController
  isEmbeded?: boolean
}

export const PartnerBasicInfo = withPartner360Checks<IPartnerBasicInfoProps>(
  ({ pid, metrics, widgetParams, isEmbeded }) => {
    const [isSavingLocations, setIsSavingLocations] = useState(false)
    const [isApplying, setIsApplying] = useState(false)
    const [isSavingDistributorBasicInfo, setIsSavingDistributorBasicInfo] = useState(false)
    const [isShowConfirmRevenueDialog, setIsShowConfirmRevenueDialog] = useState(false)

    /** ------------------------- Selectors ------------------------- */

    const isEditMode = useSelector(selectPublishingMode)
    const partnerData = useSelector(selectPartner360Data)
    const partnerAccess = useSelector(selectPartnerAccess)
    const createdBasicInfo = useSelector(selectCreatedBasicInfo)
    const defaultBasicInfo = useSelector(selectDefaultBasicInfo)
    const basicInfoLoadingStatus = useSelector(selectBasicInfoStatus)
    const partnerConfigurationLoadingStatus = useSelector(selectConfigurationLoadingStatus)
    const programType = useSelector(selectProgramType)
    const fYear = useSelector(selectFinancialYear)
    const ownerId = useSelector(selectAppOwnerId)
    const p360Profile = useSelector(selectP360Profile)
    const isCockpitPartnerDetailsVisible = useSelector(selectIsCockpitPartnerDetailsVisible)

    /** ------------------------- Hooks ------------------------- */

    const dispatch = useAppDispatch()
    const { startLoadData, stopLoadData } = useMeasureController(metrics)
    const { isPartnerUser } = useUserRoles()
    const partnerId = usePartnerId(widgetParams, pid)
    const [{ formData, modifiedFields: changedKeys }] = useGetFormData<IFormBasicInfo>(
      PARTNER_BASIC_INFO_FORM_KEY
    )
    const wizardSave = useWizardSave()
    const refetchPlanningCockpitData = useRefetchPlanningCockpitData()

    /* The section's own loader: while its record is read or written. A distributor's record, the
       row without an offering, is written without a status of the slice's own. */
    const isRecordLoading =
      basicInfoLoadingStatus === RequestStatus.PENDING || isSavingDistributorBasicInfo

    /* Only while the section's own loader is up: the completeness and the cockpit then reload
       in the background, and the section may be closed. */
    useHoldPlanSectionOpen(isApplying && isRecordLoading)

    /** ------------------------- Variables ------------------------- */

    /* The row without an offering is shown for a distributor, whose basic info is one record for
       every offering, and under the cockpit's `Overall`, which stands on no offering. */
    const isRowWithoutOffering = partnerAccess.isDistributor || programType === ProgramTypes.Overall
    const basicInfo: TBasicInfoCreated = useMemo(
      () => (isRowWithoutOffering ? { ...defaultBasicInfo, programType } : createdBasicInfo),
      [createdBasicInfo, defaultBasicInfo, isRowWithoutOffering, programType]
    )

    const isYearGoalRequired = useMemo(() => {
      return fYear >= 2024
    }, [fYear])

    const canEditPartner = useMemo(() => {
      return !isPartnerUser
    }, [isPartnerUser])

    const basicInfoChanged = useMemo(() => {
      return changedKeys.length > 0
    }, [changedKeys.length])

    const isSaveDisabled = useMemo(() => {
      return !(
        basicInfoChanged &&
        (isBasicInfoLargeFormat(basicInfo, programType === ProgramTypes.LargeFormat) ||
          formData.ppsShare === null ||
          (formData.ppsShare >= 0 && formData.ppsShare <= 100))
      )
    }, [basicInfoChanged, basicInfo, programType, formData.ppsShare])

    const isMPSorCorePrint = useMemo(
      () => [ProgramTypes.MPS, ProgramTypes.CorePrint].includes(programType),
      [programType]
    )

    const isShowApplyButton = isEditMode && canEditPartner && !wizardSave

    const isRetailer = isBasicInfoRetail(basicInfo, programType === ProgramTypes.Retail)
    const isDistributor = isBasicInfoDistributor(basicInfo, partnerAccess.isDistributor)
    const isReseller = isBasicInfoReseller(basicInfo, !isDistributor && !isRetailer)
    const isLargeFormatProgramType = isBasicInfoLargeFormat(
      basicInfo,
      programType === ProgramTypes.LargeFormat
    )
    const retailConditionsCount = Number(isRetailer)

    /** ------------------------- Methods ------------------------- */

    const saveDefaultBasicInfo = useCallback(
      async (data: IFormBasicInfo) => {
        if (!defaultBasicInfo.entryId) {
          await dispatch(createDefaultBasicInfo(data)).unwrap()
        } else {
          await dispatch(updateDefaultBasicInfo(data)).unwrap()
        }
      },
      [defaultBasicInfo.entryId, dispatch]
    )

    const applyChanges = useCallback(
      async (changes: Partial<IFormBasicInfo> = {}) => {
        setIsApplying(true)

        try {
          const dataToSave = { ...formData, ...changes }

          if (partnerAccess.isDistributor) {
            // A distributor's basic info is only the row without an offering
            setIsSavingDistributorBasicInfo(true)

            try {
              await saveDefaultBasicInfo(dataToSave)
            } finally {
              setIsSavingDistributorBasicInfo(false)
            }
          } else {
            const isOfferingChanged = createdBasicInfo.programType !== programType
            if (!createdBasicInfo.entryId || isOfferingChanged) {
              await dispatch(createBasicInfo(dataToSave)).unwrap()
            } else {
              await dispatch(updateBasicInfo(dataToSave)).unwrap()
            }
          }

          const parameters = {
            partnerId,
            fYear,
          }
          await Promise.all([
            dispatch(requestRecalculateCompleteness(parameters)),
            dispatch(requestCommitmentStatuses(parameters)),
          ])

          // Do not save Retailer data to default data, as we reuse some fields for other purposes.
          // A distributor's is saved above, as its only row.
          if (!isRetailer && !partnerAccess.isDistributor) {
            // As well as save BasicInfo in DefaultBasicInfo
            await saveDefaultBasicInfo(dataToSave)
          }

          refetchPlanningCockpitData()
        } finally {
          setIsApplying(false)
        }
      },
      [
        refetchPlanningCockpitData,
        formData,
        programType,
        saveDefaultBasicInfo,
        dispatch,
        partnerId,
        fYear,
        createdBasicInfo.entryId,
        createdBasicInfo.programType,
        isRetailer,
        partnerAccess.isDistributor,
      ]
    )

    const saveLocations = useCallback(
      async (changes: Partial<IFormBasicInfo>) => {
        setIsSavingLocations(true)
        try {
          await applyChanges(changes)
        } finally {
          setIsSavingLocations(false)
        }
      },
      [applyChanges]
    )

    const handleApplyChanges = useCallback(async () => {
      if ((formData.turnover ?? 0) > 100 || (isRetailer && (formData.operationProfit ?? 0) > 100)) {
        setIsShowConfirmRevenueDialog(true)
      } else {
        await applyChanges()
      }
    }, [applyChanges, formData.turnover, formData.operationProfit, isRetailer])

    /** ------------------------- Effects ------------------------- */

    useEffect(() => {
      if (!wizardSave) {
        return
      }
      wizardSave.registerSaveHandler(applyChanges, isSaveDisabled)

      return () => {
        wizardSave.unregisterSaveHandler()
      }
    }, [wizardSave, applyChanges, isSaveDisabled])

    useEffect(() => {
      if (wizardSave) {
        wizardSave.updateDisabled(isSaveDisabled)
      }
    }, [wizardSave, isSaveDisabled])

    useEffect(() => {
      if (partnerData.isInitializationRequired) {
        initJBPPartnerData({
          ownerId,
          partnerId,
          fYear,
          startLoadData,
          stopLoadData,
        })
      }
    }, [
      partnerData.isInitializationRequired,
      ownerId,
      partnerId,
      fYear,
      startLoadData,
      stopLoadData,
    ])

    useEffect(() => {
      if (!isEditMode) {
        dispatch(resetAllModifiedDataByKey(PARTNER_BASIC_INFO_FORM_KEY))
      }

      return () => {
        dispatch(resetAllModifiedDataByKey(PARTNER_BASIC_INFO_FORM_KEY))
      }
    }, [dispatch, isEditMode, programType, fYear, partnerId])

    /** ------------------------- Render ------------------------- */

    if (
      partnerConfigurationLoadingStatus === RequestStatus.PENDING ||
      (isRecordLoading && !isSavingLocations) ||
      partnerData.partnerId !== partnerId ||
      partnerData.entryId === 0
    ) {
      return <Loader />
    }

    const content = (
      <P360Card
        className={styles.surface}
        bodyClassName={styles.body}
        header={
          (isCockpitPartnerDetailsVisible || !isEmbeded) && (
            <CockpitCardHeader
              title={<Translation text="jbp-partner-basic-info" />}
              actions={!partnerAccess.isGlobal && <HelpButton section={HelpSection.BasicInfo} />}
            />
          )
        }
        fullHeight={true}
        disabled={isEmbeded}
      >
        <div className={styles.grid}>
          {!isCockpitPartnerDetailsVisible && (
            <div className={cn(styles.grid__item, styles['grid__item--full-width'])}>
              <BasicInfoHeader
                isRetailProgramType={isRetailer}
                isDistributor={partnerAccess.isDistributor}
                amplifyTrack={partnerData.amplifyTrack}
                isPowerElite={partnerAccess.isPowerElite}
                isPowerEliteGlobal={partnerAccess.isPowerEliteGlobal}
                p360Grades={p360Profile?.Items.P360Grades}
                specializations={p360Profile?.Items.P360Specializations}
                programs={p360Profile?.Items.P360Programs}
              />
            </div>
          )}
          {isDistributor && (
            <div className={cn(styles.grid__item, styles['grid__item--full-width'])}>
              <PartnerGroup
                partnerId={partnerId}
                fYear={fYear}
                partnerType={isDistributor ? 'Distributor' : 'Retail'}
                metrics={metrics}
                className={styles.column}
              />
            </div>
          )}
          {isDistributor && (
            <div className={cn(styles.grid__item, styles['grid__item--6-cells'])}>
              <DistributorInfo
                totalSales={basicInfo.totalSales}
                hpDedicatedSales={basicInfo.hpDedicatedSales}
                employersCount={basicInfo.employersCount}
              />
            </div>
          )}
          {isLargeFormatProgramType ? (
            <>
              <div className={cn(styles.grid__item, styles['grid__item--3-cells'])}>
                <Turnover isLargeFormatMode turnover={basicInfo.turnover} />
              </div>
              <div className={cn(styles.grid__item, styles['grid__item--3-cells'])}>
                <DemoUnits demoUnits={basicInfo.demoUnits} />
              </div>
            </>
          ) : (
            <>
              <div
                className={cn(styles.grid__item, {
                  [styles['grid__item--6-cells']]: !isRetailer,
                  [styles['grid__item--3-cells']]: isRetailer,
                })}
              >
                <Turnover
                  turnover={basicInfo.turnover}
                  ppsShare={basicInfo.ppsShare}
                  isRetail={isRetailer}
                  fYear={fYear}
                />
              </div>
              {isRetailer && (
                <div className={cn(styles.grid__item, { [styles['grid__item--3-cells']]: true })}>
                  <OperatingProfit
                    profit={basicInfo.operationProfit}
                    yoy={basicInfo.operationProfitYoY}
                    fYear={fYear}
                  />
                </div>
              )}
            </>
          )}
          {isReseller && (
            <div
              className={cn(styles.grid__item, {
                [styles['grid__item--4-cells']]: isMPSorCorePrint,
                [styles['grid__item--6-cells']]: !isMPSorCorePrint,
              })}
            >
              <PartnerHead
                salesCount={basicInfo.salesCount}
                serviceCount={basicInfo.serviceCount}
              />
            </div>
          )}
          {isMPSorCorePrint && (
            <div
              className={cn(styles.grid__item, {
                [styles['grid__item--2-cells']]: true,
              })}
            >
              <ServiceDelivery isServiceDelivery={partnerData.isIMPSServiceDelivery} />
            </div>
          )}
          {isRetailer && (
            <>
              <div className={cn(styles.grid__item, { [styles['grid__item--2-cells']]: true })}>
                <Ticker ticker={basicInfo.ticker} />
              </div>
              <div
                className={cn(styles.grid__item, {
                  [styles['grid__item--6-cells']]:
                    retailConditionsCount === 2 || retailConditionsCount === 0,
                  [styles['grid__item--4-cells']]: retailConditionsCount === 1,
                })}
              >
                <RetailInfo
                  employersCount={basicInfo.employersCount}
                  salesCount={basicInfo.salesCount}
                />
              </div>
              <div
                className={cn(styles.grid__item, styles['grid__item--6-cells'], styles.location)}
              >
                <Branches
                  isApplicable={basicInfo.branchesIsApplicable}
                  count={basicInfo.branchesCount}
                  locations={basicInfo.branchesLocations}
                  isDistributor={isDistributor}
                  isRetail={isRetailer}
                />
              </div>
              <div
                className={cn(styles.grid__item, styles['grid__item--6-cells'], styles.location)}
              >
                <Showrooms
                  isApplicable={basicInfo.showroomsIsApplicable}
                  count={basicInfo.showroomsCount}
                  locations={basicInfo.showroomsLocations}
                  isRetail={isRetailer}
                />
              </div>
              <div className={cn(styles.grid__item, { [styles['grid__item--6-cells']]: true })}>
                <RetailerStores
                  storesTotal={basicInfo.storesTotal}
                  storesPSHW={basicInfo.storesPSHW}
                  storesPRHW={basicInfo.storesPRHW}
                />
              </div>
            </>
          )}
          {!isRetailer && (
            <div
              className={cn(styles.grid__item, styles['grid__item--6-cells'], styles.location, {
                [styles.distributorLocations]: isDistributor,
              })}
            >
              <Branches
                isApplicable={basicInfo.branchesIsApplicable}
                count={basicInfo.branchesCount}
                locations={basicInfo.branchesLocations}
                isDistributor={isDistributor}
                isRetail={isRetailer}
              />
            </div>
          )}
          {isReseller && (
            <div className={cn(styles.grid__item, styles['grid__item--6-cells'], styles.location)}>
              <Showrooms
                isApplicable={basicInfo.showroomsIsApplicable}
                count={basicInfo.showroomsCount}
                locations={basicInfo.showroomsLocations}
              />
            </div>
          )}
          {isDistributor && (
            <div className={cn(styles.grid__item, styles['grid__item--6-cells'], styles.location)}>
              <Warehouses
                isApplicable={basicInfo.warehouseIsApplicable}
                count={basicInfo.warehouseCount}
                locations={basicInfo.warehouseLocations}
              />
            </div>
          )}
          {!isRetailer && (
            <div
              className={cn(styles.grid__item, styles.editor, {
                [styles['grid__item--7-cells']]: isDistributor,
                [styles['grid__item--6-cells']]: !isDistributor,
              })}
            >
              <ExecutiveSummary
                isRetail={isRetailer}
                isDistributor={isDistributor}
                executiveSummary={basicInfo.summary}
              />
            </div>
          )}
          {isRetailer && (
            <>
              <div className={cn(styles.grid__item, styles['grid__item--full-width'])}>
                <RetailerFinance
                  pyActuals={partnerData.pyActuals}
                  cyPlans={partnerData.cyPlans}
                  fYear={fYear}
                />
              </div>
              <div className={cn(styles.grid__item, styles['grid__item--full-width'])}>
                <RetailerSummary
                  isEditMode={isEditMode}
                  initialValue={basicInfo.summary || ''}
                  labelKey={TRANSLATION_KEYS.retailerSummary}
                />
              </div>
            </>
          )}
          {!isDistributor && !isRetailer && (
            <div className={cn(styles.grid__item, styles['grid__item--6-cells'], styles.editor)}>
              <OneKeyDirection
                isRetail={isRetailer}
                isRequiredForCompleteness={isYearGoalRequired}
                oneKeyDirection={basicInfo.achievement}
              />
            </div>
          )}
          {isDistributor && (
            <div className={cn(styles.grid__item, styles['grid__item--5-cells'], styles.editor)}>
              <ProductExhibits exhibits={basicInfo.exhibits} />
            </div>
          )}
        </div>

        {isShowApplyButton && (
          <footer className={styles.footer}>
            <ApplyButton
              className={styles.applyButton}
              disabled={isSaveDisabled}
              onClick={handleApplyChanges}
            />
          </footer>
        )}

        <ConfirmRevenueDialog
          isShowDialog={isShowConfirmRevenueDialog}
          isRetailer={isRetailer}
          onConfirm={async () => {
            setIsShowConfirmRevenueDialog(false)
            await applyChanges()
          }}
          onClose={() => {
            setIsShowConfirmRevenueDialog(false)
          }}
        />
      </P360Card>
    )

    return (
      <BasicInfoSaveContext.Provider value={saveLocations}>{content}</BasicInfoSaveContext.Provider>
    )
  },
  {
    accessOverlayPlaceholder: <P360Access />,
    scopeOverlayPlaceholder: (
      <P360NotInScopeWidgetPlaceholder showMessage className={styles.placeholderContainer} />
    ),
  }
)

export default PartnerBasicInfo
